import React, { useState } from "react";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Box,
  Text,
  Badge,
  Button,
  HStack,
  IconButton,
  Flex,
  Spacer,
} from "@chakra-ui/react";
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa6'

const ReturnHistoryTable = ({ returnHistory }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Función para formatear la fecha
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  // Función para formatear la hora
  const formatTime = (timeString) => {
    if (!timeString) return "-";
    return timeString.substring(0, 5); // Formato HH:mm
  };

  // Si no hay datos de historial, mostrar un mensaje
  if (!returnHistory || returnHistory.length === 0) {
    return (
      <Box p={4} textAlign="center">
        <Text color="gray.500">No hay historial de devoluciones disponible</Text>
      </Box>
    );
  }

  // Calcular paginación
  const totalPages = Math.ceil(returnHistory.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = returnHistory.slice(startIndex, endIndex);

  // Funciones de navegación
  const goToNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  const goToPreviousPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const goToPage = (page) => {
    setCurrentPage(page);
  };

  return (
    <Box mb={4}>
      <Flex mb={3} align="center">
        <Text fontSize="lg" fontWeight="bold">
          Historial de Devoluciones
        </Text>
        <Spacer />
        <Text fontSize="sm" color="gray.500">
          {returnHistory.length} registro{returnHistory.length !== 1 ? 's' : ''} total{returnHistory.length !== 1 ? 'es' : ''}
        </Text>
      </Flex>
      
      <TableContainer>
        <Table variant="simple" size="sm">
          <Thead>
            <Tr>
              <Th>N°</Th>
              <Th>Tipo de Devolución</Th>
              <Th>Motivo</Th>
              <Th>Usuario</Th>
              <Th>Fecha</Th>
            </Tr>
          </Thead>
          <Tbody>
            {currentItems.map((returnItem, index) => (
              <Tr key={returnItem.id}>
                <Td>
                  <Badge colorScheme="orange" variant="outline">
                    {startIndex + index + 1}
                  </Badge>
                </Td>
                <Td>
                  <Text fontWeight="medium">
                    {returnItem.tipo_devolucion || '-'}
                  </Text>
                </Td>
                <Td>
                  <Text fontSize="sm" maxW="300px" isTruncated title={returnItem.reason_for_return}>
                    {returnItem.reason_for_return || '-'}
                  </Text>
                </Td>
                <Td>
                  <Text fontSize="sm">
                    {returnItem.nickname || returnItem.person_name || '-'}
                    {returnItem.email && (
                      <>
                        <br />
                        <Text as="span" color="gray.500" fontSize="xs">
                          {returnItem.email}
                        </Text>
                      </>
                    )}
                  </Text>
                </Td>
                <Td>
                  <Text fontSize="sm">
                    {returnItem.date_return || '-'}
                    {returnItem.time_return && (
                      <>
                        <br />
                        <Text as="span" color="gray.500">
                          {formatTime(returnItem.time_return)}
                        </Text>
                      </>
                    )}
                  </Text>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>

      {/* Controles de Paginación */}
      {totalPages > 1 && (
        <Flex mt={4} justify="center" align="center">
          <HStack spacing={2}>
            <IconButton
              icon={<FaChevronLeft />}
              onClick={goToPreviousPage}
              isDisabled={currentPage === 1}
              size="sm"
              variant="outline"
              aria-label="Página anterior"
            />
            
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <Button
                key={page}
                onClick={() => goToPage(page)}
                size="sm"
                variant={currentPage === page ? "solid" : "outline"}
                colorScheme={currentPage === page ? "orange" : "gray"}
              >
                {page}
              </Button>
            ))}
            
            <IconButton
              icon={<FaChevronRight />}
              onClick={goToNextPage}
              isDisabled={currentPage === totalPages}
              size="sm"
              variant="outline"
              aria-label="Página siguiente"
            />
          </HStack>
        </Flex>
      )}
      
      {/* Información de paginación */}
      {totalPages > 1 && (
        <Text textAlign="center" mt={2} fontSize="sm" color="gray.500">
          Mostrando {startIndex + 1}-{Math.min(endIndex, returnHistory.length)} de {returnHistory.length} registros
        </Text>
      )}
    </Box>
  );
};

export default ReturnHistoryTable;
